import { addDays, isFuture, startOfDay } from 'date-fns';
import { environment } from '~env';
import { REPORT_NAMES } from '../constants/reports.constant';
import { Brand } from '../enums/brand.enum';
import { Role } from '../enums/role.enum';
import { ISession } from '../services/auth/Session.interface';
import { IPolicy } from '../services/mw/interfaces/GetSignOnPoliciesAcknowledgement.interface';
import { Assignment } from './assignment';
import { CrewEffects } from './crewEffects.model';
import { CrewPhysique } from './crewPhysique.model';
import { Profile } from './profile.model';
import { Ship } from './ship.model';
export class User {
  public assignments: Array<Assignment>;
  public profile: Profile;
  public crewPhysique: CrewPhysique;
  public crewEffects: CrewEffects;
  public managerShip?: Ship;
  public signedCheckInPolicies?: IPolicy[];
  public signedCheckOutPolicies?: IPolicy[];

  public readonly data: ISession;

  constructor(private readonly token?: string, private readonly crewMemberId?: string) {
    if (this.token) {
      this.data = this.extractSessionFromJWT(this.token);
    } else if (this.crewMemberId) {
      this.data = {
        exp: 0,
        payload: {
          Company: {
            Code: '',
          },
          Roles: [Role.User],
          sessionid: {
            pre_check: {
              data: [
                {
                  user: this.crewMemberId,
                  sid: '',
                },
              ],
            },
          },
        },
      };
    }
  }

  public get isSessionValid(): boolean {
    const expiration = this.data.exp * 1000;
    return isFuture(expiration);
  }

  public get id(): string {
    return this.data.payload.sessionid.pre_check.data[0].user;
  }

  public get brand(): Brand {
    const currentBrandValue = this.data.payload.Company.Code;
    return Object.values(Brand).find(brandValue => brandValue === currentBrandValue);
  }

  public get sessionId(): string {
    return this.data.payload.sessionid.pre_check.data[0].sid;
  }

  public get isCrewMember(): boolean {
    return this.role === Role.User;
  }

  public get isManager(): boolean {
    for (const role of environment.auth.managerGroup) {
      if (this.role === role) return true;
    }
    return false;
  }

  public get isAdmin(): boolean {
    return this.role === Role.Admin;
  }

  public get isHRManager(): boolean {
    return this.role === Role.HRManager;
  }

  public get isHRTeam(): boolean {
    return this.role === Role.HRTeam;
  }

  public get isMedical(): boolean {
    return this.role === Role.Medical;
  }

  public get isFinance(): boolean {
    return this.role === Role.Finance;
  }

  public get isCrewAdminManager(): boolean {
    return this.role === Role.CrewAdminManager;
  }

  public get role(): Role {
    const currentRoleValue = this.data.payload.Roles[0];
    return Object.values(Role).find(roleValue => roleValue === currentRoleValue);
  }

  public get managerShipCode(): string {
    try {
      return this.data.payload.Ship.Code;
    } catch (error) {
      return undefined;
    }
  }

  public get currentSignedCheckInPolicies(): IPolicy[] {
    return this.signedCheckInPolicies ?? [];
  }

  public get currentSignedCheckOutPolicies(): IPolicy[] {
    return this.signedCheckOutPolicies ?? [];
  }

  public get activeAssignment(): Assignment {
    const d = new Date();
    return this.assignments?.find(assignment => {
      return assignment.signOnDate <= d && assignment.signOffDate >= d;
    });
  }

  public get nextFutureAssignment(): Assignment {
    return this.upcomingAssignments?.[0];
  }

  public get nextFutureSignOffAssignment(): Assignment {
    return this.upcomingCheckoutAssignments?.[0];
  }

  public get upcomingAssignments(): Array<Assignment> {
    return this.assignments
      ?.filter(assignment => {
        const today = startOfDay(new Date());
        if (assignment.signOnDate < today) return false;
        return true;
      })
      .sort((a, b) => {
        if (a.signOnDate > b.signOnDate) return 1;
        if (a.signOnDate < b.signOnDate) return -1;
        return 0;
      });
  }

  public get upcomingCheckoutAssignments(): Assignment[] {
    return this.assignments
      ?.filter(assignment => {
        const today = startOfDay(new Date());
        if (assignment.signOffDate < today) return false;
        return true;
      })
      .sort((a, b) => {
        if (a.signOffDate > b.signOffDate) return 1;
        if (a.signOffDate < b.signOffDate) return -1;
        return 0;
      });
  }

  public get lastReleasedAssignment(): Assignment {
    const today = startOfDay(new Date());
    const releasedAssignments = this.assignments
      .filter(assignment => assignment.contractReleased && addDays(assignment.signOffDate, 1) >= today)
      .sort((a, b) => {
        if (a.signOnDate < b.signOnDate) return 1;
        if (a.signOnDate > b.signOnDate) return -1;
        return 0;
      });
    return releasedAssignments[0]; // get last released contract
  }

  public isLastReleasedAssignment(assignment: Assignment): boolean {
    return this.lastReleasedAssignment === assignment;
  }

  public findAssignment(shipCode: string, signonDate: string): Assignment {
    return this.assignments.find(assignment => {
      try {
        return assignment.ship.code === shipCode && assignment.data.signonDate === signonDate;
      } catch (error) {}
    });
  }

  public canSeeReport(reportId: keyof typeof REPORT_NAMES, selectedRole?: Role): boolean {
    const role = selectedRole || this.role;
    switch (reportId) {
      case 'crewCheckinInfo':
      case 'healthCheck':
      case 'missingSharepointSignons':
      case 'delayedEnvelope':
        return role === Role.Admin;
      case 'policyAcknowledgement':
        return role === Role.Admin || role === Role.Finance || this.isHRSignatory(role);
    }
  }

  public isHRSignatory(selectedRole?: Role): boolean {
    const role = selectedRole || this.role;
    switch (this.brand) {
      case Brand.RCCL:
      case Brand.CELEBRITY:
        return role === Role.CrewAdminManager;
      case Brand.AZAMARA:
        return role === Role.HRManager;
    }
  }

  public extractSessionFromJWT(token: string): ISession {
    let session: ISession = JSON.parse(atob(token.split('.')[1]));
    if (!session.exp) throw new Error('Missing session expiration');
    if (!session.payload.sessionid.pre_check.data[0].user) throw new Error('Missing session crew member id');
    if (!session.payload.sessionid.pre_check.data[0].sid) throw new Error('Missing session id');
    if (!session.payload.Roles) throw new Error('Missing session roles');
    session = this.hasAdminGroupRoles(session);
    if (!session.payload.Company) throw new Error('Missing session company');
    if (!session.payload.Company.Code) throw new Error('Missing session company code');
    session = this.hasManagerGroupRoles(session);
    return session;
  }

  public hasAdminGroupRoles(session: ISession): ISession {
    if (session.payload.Roles.includes(environment.auth.adminGroup)) {
      if (!session?.payload?.Ship || !session?.payload?.Ship?.Code) {
        session.payload.Ship = {
          Code: 'AL',
        };
      }
      session.payload.Company = {
        Code: Brand.RCCL,
      };
    }
    return session;
  }

  public hasManagerGroupRoles(session: ISession): ISession {
    for (const role of environment.auth.managerGroup) {
      if (session.payload.Roles.includes(role)) {
        if (!session.payload.Ship) throw new Error('Missing session ship');
        if (!session.payload.Ship.Code) throw new Error('Missing session ship code');
        break;
      }
    }
    return session;
  }
}
